import { NewButton } from '@/components/basic';
import { DeleteResourceModal } from '@/components/partial/Modals/DeleteResourceModal';
import { EditMultipleTextInput } from '@/components/resources/EditMultipleTextInput';
import { SelectEditBooleanMultipleValues } from '@/components/resources/SelectEditBooleanMultipleValues';
import LoadingSpinner from '@/components/util/LoadingSpinner';
import { api } from '@/lib/api/api';
import { classNames } from '@/lib/classNames';
import {
  hasEnabledDeleteResource,
  isLabelFieldEnabled
} from '@/lib/featureFlags';
import { replaceVarsBulkName } from '@/lib/utils';
import { EditResourceRequest, IResource } from '@gettactic/api';
import { useLoadingToast } from '@gettactic/helpers/src/hooks/toasts/useLoadingToast';
import { useAuthenticated } from '@gettactic/hooks';
import { TrashIcon } from '@heroicons/react/outline';
import { ScrollArea } from '@mantine/core';
import { BaseSyntheticEvent, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

type Props = {
  resources: IResource[];
  mutate: () => void;
  notifyUpdateLabels?: (resWithLabels: IResource[]) => void;
  onSaved?: () => void;
};
export const RESOURCE_VALUE_NOT_EQUAL = 'NOT_EQUAL';
export type ResourceValueNotEqualType = typeof RESOURCE_VALUE_NOT_EQUAL;

export type EditResourceMultipleForm = {
  bulkValues: {
    is_restricted: boolean;
    is_approvable: boolean;
    is_unavailable: boolean;
  }[];
  textBulkValues: {
    label: string;
    description: string;
    name: string;
  };
};

export function GeneralTabMultiple({
  resources,
  mutate,
  notifyUpdateLabels,
  onSaved
}: Props) {
  const toast = useLoadingToast({
    loadingMessage: 'Applying changes...'
  });
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const [deleteModal, setDeleteModal] = useState([] as IResource[]);
  const defaultValues: EditResourceMultipleForm = {
    bulkValues: resources.map((x) => ({
      is_restricted: !x.is_open,
      is_approvable: x.is_approvable,
      is_unavailable: !x.is_available
    })),
    textBulkValues: {
      label: '',
      description: '',
      name: ''
    }
  };
  const { control, watch, handleSubmit, setValue } =
    useForm<EditResourceMultipleForm>({
      defaultValues
    });

  const bulkValuesLabel = watch('textBulkValues.label');
  useEffect(() => {
    if (!notifyUpdateLabels) {
      return;
    }
    const replacedLabels = resources.map((x, i) => ({
      ...x,
      label: bulkValuesLabel
        ? replaceVarsBulkName(bulkValuesLabel, resources.length - i - 1)
        : x.label
    }));
    notifyUpdateLabels(replacedLabels);
  }, [bulkValuesLabel, resources, notifyUpdateLabels]);
  const [loading, setLoading] = useState<boolean>(false);
  const onEditSubmit = async (
    data: EditResourceMultipleForm,
    event?: BaseSyntheticEvent<unknown, HTMLFormElement, HTMLFormElement>
  ) => {
    setLoading(true);
    toast.startLoading();
    let hasError = 0;

    for (let i = 0; i < resources.length; i++) {
      const resource = resources[i];
      const insertIndex = resources.length - i - 1;
      const request: EditResourceRequest = {
        type: resource.type,
        parent_id: resource.parent_id,
        name:
          data.textBulkValues.name !== ''
            ? replaceVarsBulkName(data.textBulkValues.name, insertIndex)
            : resource.name,
        label:
          data.textBulkValues.label !== ''
            ? replaceVarsBulkName(data.textBulkValues.label, insertIndex)
            : resource.label,
        description:
          data.textBulkValues.description !== ''
            ? replaceVarsBulkName(data.textBulkValues.description, insertIndex)
            : resource.description,
        total_slots: resource.total_slots,
        maximum_slots_per_user: resource.maximum_slots_per_user,
        is_open: !data.bulkValues[i].is_restricted,
        is_approvable: data.bulkValues[i].is_approvable,
        is_available: !data.bulkValues[i].is_unavailable,
        // TODO: Add this field to the UI
        is_shareable: resource.is_shareable,
        weight: resource.weight,
        map_url: resource.map_url
      };
      const { ok } = await api.client.organizations.resourcePut(
        resource.id,
        request
      );
      setLoading(false);

      if (!ok) {
        hasError++;
      }
      mutate();
    }
    if (hasError === 0) {
      onSaved?.();
      toast.success('Changes saved');
    } else {
      toast.error("Sorry we couldn't save all the changes");
    }
  };

  return (
    <div>
      <DeleteResourceModal
        deleteModal={deleteModal}
        setDeleteModal={setDeleteModal}
      />

      <form
        id="resourceSettingsPanel"
        className="flex flex-auto flex-col flex-nowrap overflow-hidden"
        onSubmit={handleSubmit(onEditSubmit)}
      >
        <div className="">
          <ScrollArea type="auto" offsetScrollbars className="h-full">
            <h4 className="my-4 text-lg font-bold text-secondary">
              Edit multiple resources
            </h4>
            <div className="mt-4 flex flex-col space-y-4 px-2">
              <EditMultipleTextInput
                name="name"
                defaultValues={defaultValues}
                control={control}
                setValue={setValue}
                label="Name"
              />
              <div
                className={classNames(
                  !isLabelFieldEnabled(authenticatedUser) && 'hidden'
                )}
              >
                <EditMultipleTextInput
                  name="label"
                  defaultValues={defaultValues}
                  control={control}
                  setValue={setValue}
                  label="Label"
                />
              </div>
              <EditMultipleTextInput
                name="description"
                defaultValues={defaultValues}
                control={control}
                setValue={setValue}
                label="Description"
              />
              <SelectEditBooleanMultipleValues
                name="is_restricted"
                defaultValues={defaultValues}
                control={control}
                resources={resources}
                setValue={setValue}
                label="Assigned"
                help="When this box is checked, only assigned teams &amp; users will be able to make reservations."
              />

              <SelectEditBooleanMultipleValues
                name="is_approvable"
                defaultValues={defaultValues}
                control={control}
                resources={resources}
                setValue={setValue}
                label="Approval Required"
                help="When this box is checked, reservations will need to be approved by an admin or team manager."
              />

              <SelectEditBooleanMultipleValues
                name="is_unavailable"
                defaultValues={defaultValues}
                control={control}
                resources={resources}
                setValue={setValue}
                label="Make unavailable"
                help="When this box is checked, no new reservations can be made."
                color="red"
              />
            </div>
          </ScrollArea>
        </div>
        <div className="my-4 flex flex-nowrap gap-3">
          <NewButton.Component
            className="w-full sm:w-auto sm:flex-1"
            variant={NewButton.Variant.PrimarySquared}
            theme={NewButton.Theme.Primary}
            size={NewButton.Size.Medium}
            type="submit"
            form="resourceSettingsPanel"
            disabled={loading}
          >
            {loading ? <LoadingSpinner color="text-white" /> : <>Save</>}
          </NewButton.Component>
          {hasEnabledDeleteResource(authenticatedUser) ? (
            <NewButton.Component
              className="w-full flex-none sm:w-auto"
              variant={NewButton.Variant.PrimarySquared}
              theme={NewButton.Theme.Red}
              size={NewButton.Size.Medium}
              disabled={loading}
              onClick={(ev) => {
                ev.preventDefault();
                setDeleteModal(resources);
              }}
            >
              <TrashIcon className="h-6 w-6" />
            </NewButton.Component>
          ) : null}
        </div>
      </form>
    </div>
  );
}
